import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

interface LoyaltyStats {
  totalPoints: number;
  totalRides: number;
  totalDistance: number;
  currentLevel: string;
  nextLevel: string;
  pointsToNext: number;
  loyaltyNumber?: string;
  memberName?: string;
  recentActivity?: Array<{
    points: number;
    activityType: string;
    earnedDate: string;
  }>;
}

const LoyaltyProgramPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loyaltyStats, setLoyaltyStats] = useState<LoyaltyStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLoyaltyStats();
  }, []);

  const fetchLoyaltyStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/users/loyalty');
      setLoyaltyStats(response.data);
    } catch (error) {
      console.error('Error fetching loyalty stats:', error);
      toast.error(t('profile.loyaltyLoadError'));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  const formatActivityType = (activityType: string): string => {
    return activityType
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center">
          <div className="text-lg">{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (!loyaltyStats) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/profile')}
          className="mb-4 text-primary-600 hover:text-primary-700"
        >
          ← {t('common.back')}
        </button>
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="text-6xl mb-4">🏆</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('profile.noLoyaltyData')}</h2>
          <p className="text-gray-600">{t('profile.startRidingToEarn')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <button
        onClick={() => navigate('/profile')}
        className="mb-4 text-primary-600 hover:text-primary-700"
      >
        ← {t('common.back')}
      </button>

      {/* Header Card */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center space-x-4">
          <div className="text-5xl">🏆</div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">{loyaltyStats.memberName}</h1>
            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-800 text-sm font-medium rounded-full">
              {loyaltyStats.loyaltyNumber}
            </span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className="text-4xl mb-2">⭐</div>
          <p className="text-3xl font-bold text-primary-600 mb-1">{loyaltyStats.totalPoints}</p>
          <p className="text-sm text-gray-600">{t('profile.totalPoints')}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className="text-4xl mb-2">🚴</div>
          <p className="text-3xl font-bold text-primary-600 mb-1">{loyaltyStats.totalRides}</p>
          <p className="text-sm text-gray-600">{t('profile.ridesCompleted')}</p>
        </div>
      </div>

      {/* Total Distance */}
      {loyaltyStats.totalDistance > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center space-x-4">
            <div className="text-4xl">🗺️</div>
            <div>
              <p className="text-3xl font-bold text-gray-900 mb-1">
                {loyaltyStats.totalDistance.toFixed(1)} km
              </p>
              <p className="text-sm text-gray-600">{t('profile.totalDistance')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {loyaltyStats.recentActivity && Array.isArray(loyaltyStats.recentActivity) && loyaltyStats.recentActivity.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('profile.recentActivity')}</h2>
          <div className="space-y-0 divide-y divide-gray-200">
            {loyaltyStats.recentActivity.map((activity: any, index: number) => (
              <div key={index} className="py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`text-2xl ${activity.points > 0 ? 'text-green-500' : 'text-red-500'}`}>
                    {activity.points > 0 ? '➕' : '➖'}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{formatActivityType(activity.activityType)}</p>
                    <p className="text-sm text-gray-500">{formatDate(activity.earnedDate)}</p>
                  </div>
                </div>
                <div className={`text-lg font-bold ${activity.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {activity.points > 0 ? '+' : ''}{activity.points}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Card */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-start space-x-4">
          <div className="text-3xl">ℹ️</div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('profile.howToEarnPoints')}</h3>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>• {t('profile.earnPointsJoinRide')}</li>
              <li>• {t('profile.earnPointsCompleteRide')}</li>
              <li>• {t('profile.earnPointsBirthday')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoyaltyProgramPage;
